import { useBooleanState } from '@cian/react-utils';
import { ModalWindow } from '@cian/ui-kit';

import { FC, useCallback, useState } from 'react';

import { useFetchPostApplyForPublication } from 'shared/hooks/useFetchPostApplyForPublication';

import { CoworkingPlacementApplicationForm } from '../CoworkingPlacementApplicationForm';
import { ModalContentStageFilling } from '../ModalContentStageFilling';
import { ModalContentStageSuccess } from '../ModalContentStageSuccess';

import { EStageTypes, MODAL_MAX_WIDTH, MODAL_WIDTH, formStages } from './constants';
import { normalizePlacementApplicationFormFields } from './internal/helpers/normalizePlacementApplicationFormFields';
import { useCoworkingPlacementApplicationModalBoundActions } from './internal/hooks/useCoworkingPlacementApplicationModalBoundActions';
import { HandleStageFillingNext, ICoworkingPlacementApplicationModalProps } from './types';

import * as styles from './CoworkingPlacementApplicationModal.css';

export const CoworkingPlacementApplicationModal: FC<ICoworkingPlacementApplicationModalProps> = props => {
  const { children } = props;

  const boundActions = useCoworkingPlacementApplicationModalBoundActions();

  const { callRequest: callApplyForPublicationRequest, pending: applyForPublicationPending } =
    useFetchPostApplyForPublication();

  const [formStageState, setFormStageState] = useState<EStageTypes>(EStageTypes.filling);

  const modalVisibilityState = useBooleanState();
  const { setFalse: setFalseModalVisibility, state: isModalVisible } = modalVisibilityState;

  const handleStageFillingNext = useCallback<HandleStageFillingNext>(
    formState => {
      const requestParameters = normalizePlacementApplicationFormFields(formState);

      callApplyForPublicationRequest(
        { parameters: requestParameters },
        {
          onSuccess() {
            setFormStageState(formStages[formStageState].next);
            boundActions.trackPlacementSend();
          },
        },
      );
    },
    [boundActions, callApplyForPublicationRequest, formStageState],
  );

  const handleModalClose = useCallback<VoidFunction>(() => {
    setFormStageState(EStageTypes.filling);
    setFalseModalVisibility();
  }, [setFalseModalVisibility]);

  const modalContent = (() => {
    switch (formStageState) {
      case EStageTypes.filling:
        return (
          <ModalContentStageFilling
            description="Чтобы получить доступ к размещению коворкинга, оставьте свои контакты. Наш специалист свяжется с вами в течение
        2 рабочих дней, уточнит детали и всё подробно расскажет."
            title="Заявка на размещение коворкинга"
          >
            <CoworkingPlacementApplicationForm
              disableFieldsOnPending
              pending={applyForPublicationPending}
              onSubmit={handleStageFillingNext}
            />
          </ModalContentStageFilling>
        );

      case EStageTypes.success:
        return <ModalContentStageSuccess descriptionType="specialist" onNextStage={handleModalClose} />;

      // istanbul ignore next
      default:
        return (
          <ModalContentStageFilling
            description="Чтобы получить доступ к размещению коворкинга, оставьте свои контакты. Наш специалист свяжется с вами в течение
        2 рабочих дней, уточнит детали и всё подробно расскажет."
            title="Заявка на размещение коворкинга"
          >
            <CoworkingPlacementApplicationForm onSubmit={handleStageFillingNext} />
          </ModalContentStageFilling>
        );
    }
  })();

  return (
    <>
      <ModalWindow
        escape
        fixed={false}
        maxWidth={MODAL_MAX_WIDTH}
        open={isModalVisible}
        outside={formStageState !== EStageTypes.filling}
        size="M"
        width={MODAL_WIDTH}
        onClose={handleModalClose}
      >
        <div className={styles['content-container']} data-testid="placement-coworking-modal-content">
          {modalContent}
        </div>
      </ModalWindow>
      {children(modalVisibilityState)}
    </>
  );
};

CoworkingPlacementApplicationModal.displayName = 'CoworkingPlacementApplicationModal';
